import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Scissors, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Select, Textarea, TimePicker } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const ANESTHESIA_TYPES = ['general', 'spinal', 'local', 'regional', 'sedation'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const INITIAL = {
  patientId: '',
  theatreId: '',
  surgeonId: '',
  anesthetistId: '',
  procedureName: '',
  time: '09:00',
  estimatedMinutes: 60,
  anesthesiaType: 'general',
  notes: '',
};

export function ScheduleCasePage() {
  const [params] = useSearchParams();
  const requestedDate = params.get('date') ?? '';
  const date = ISO_DATE.test(requestedDate) ? requestedDate : todayIso();
  const returnTo = safeReturnTo(params.get('returnTo'), '/ot');
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState<string | null>(null);

  const theatres = useQuery({
    queryKey: ['theatres'],
    queryFn: () => get<{ id: string; code: string; name: string }[]>('/ot/theatres'),
  });

  const doctors = useQuery({
    queryKey: ['doctors'],
    queryFn: async () => (await get<{ data: { id: string; name: string }[] }>('/org/clinicians?kind=doctors&limit=200')).data,
  });

  const schedule = useMutation({
    mutationFn: () =>
      post('/ot/cases', {
        patientId: form.patientId,
        theatreId: form.theatreId,
        surgeonId: form.surgeonId,
        anesthetistId: form.anesthetistId || null,
        procedureName: form.procedureName,
        scheduledStart: new Date(`${date}T${form.time}:00`).toISOString(),
        estimatedMinutes: form.estimatedMinutes,
        anesthesiaType: form.anesthesiaType,
        notes: form.notes || undefined,
      }),
    onSuccess: () => {
      toast.success('Case listed');
      void queryClient.invalidateQueries({ queryKey: ['ot-cases'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.patientId) && Boolean(form.theatreId) && Boolean(form.surgeonId) && form.procedureName.trim().length >= 2;
  const dirty = !schedule.isSuccess && JSON.stringify(form) !== JSON.stringify(INITIAL);

  return (
    <FormScreen
      title="Schedule a case"
      description="Overlapping bookings in the same theatre are refused"
      eyebrow="Operation theatre"
      icon={Scissors}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        schedule.mutate();
      }}
    >
      <FormSection title="Patient" icon={UserRound}>
        <Field label="Patient" required className="sm:col-span-2">
          <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
        </Field>
        <Field label="Procedure" required className="sm:col-span-2">
          <Input value={form.procedureName} onChange={(e) => setForm({ ...form, procedureName: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title="Slot" icon={CalendarClock}>
        <Field label="Theatre" required>
          <Select value={form.theatreId} onChange={(e) => setForm({ ...form, theatreId: e.target.value })}>
            <option value="">Select…</option>
            {theatres.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Start" required hint={`On ${date}`}>
          <TimePicker value={form.time} onChange={(value) => setForm({ ...form, time: value })} />
        </Field>
        <Field label="Surgeon" required>
          <Select value={form.surgeonId} onChange={(e) => setForm({ ...form, surgeonId: e.target.value })}>
            <option value="">Select…</option>
            {doctors.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Anaesthetist">
          <Select value={form.anesthetistId} onChange={(e) => setForm({ ...form, anesthetistId: e.target.value })}>
            <option value="">Not assigned</option>
            {doctors.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Estimated minutes">
          <Input
            type="number"
            min={10}
            max={1440}
            value={form.estimatedMinutes}
            onChange={(e) => setForm({ ...form, estimatedMinutes: Number(e.target.value) })}
          />
        </Field>
        <Field label="Anaesthesia">
          <ChoiceButtons<string>
            layout="chips"
            value={form.anesthesiaType}
            onChange={(value) => setForm({ ...form, anesthesiaType: value })}
            options={ANESTHESIA_TYPES.map((a) => ({ value: a, label: humanize(a, 'anesthesia_type') }))}
          />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="List case" loading={schedule.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
